import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  ClipboardList, RefreshCw, Search, Loader2, Play, Truck, Split,
  CheckCircle2, XCircle, Clock, AlertTriangle, ChevronLeft, ChevronRight, Radio, Bookmark, X, Warehouse,
} from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';
import PartialShipmentModal from './PartialShipmentModal';
import ShippingLabelModal from './ShippingLabelModal';

// Phase 5.3 · 실전 주문 작업대 + Phase 5.3B · 저장된 보기 · 실패 상세 · 키보드

const WORK_STATUS_ORDER = ['new_paid','shippable','partial_shippable','waiting_stock','picking','inspected','awaiting_tracking','shipped_done','has_out_of_stock'];
const WS_LABEL = {
  new_paid: '신규 결제', shippable: '출고 가능', partial_shippable: '부분출고 가능',
  waiting_stock: '입고 대기', picking: '피킹중', inspected: '검수완료',
  awaiting_tracking: '송장대기', shipped_done: '출고완료', has_out_of_stock: '품절 포함',
};
const WS_CLS = {
  new_paid: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-300',
  shippable: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300',
  partial_shippable: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:border-indigo-500/30 dark:text-indigo-300',
  waiting_stock: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300',
  picking: 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-500/10 dark:border-amber-500/30',
  inspected: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-300',
  awaiting_tracking: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-300',
  shipped_done: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-white/5 dark:border-white/10',
  has_out_of_stock: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-300',
};
const SORT_OPTIONS = [
  { value: 'created_desc',    label: '최신 주문순' },
  { value: 'created_asc',     label: '오래된 주문순' },
  { value: 'payment_pending', label: '결제대기 우선' },
  { value: 'shortage_first',  label: '재고부족 우선' },
  { value: 'shippable_first', label: '출고대기 우선' },
  { value: 'amount_desc',     label: '금액 높은순' },
];
const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;
const _mask = (p) => { if (!p) return '-'; const d = String(p).replace(/\D/g, ''); if (d.length < 8) return '***'; return `${d.slice(0,3)}-****-${d.slice(-4)}`; };
const fmt = (d) => { if (!d) return '-'; try { return format(new Date(d), 'MM.dd HH:mm', { locale: ko }); } catch { return '-'; } };
const firstShortageSku = (row) => {
  const items = row.items || [];
  const bad = items.find((it) => it.fulfillment_status === 'waiting_stock' || it.fulfillment_status === 'out_of_stock');
  return bad?.sku || '';
};

export default function OmsWorkflowSection({ onOpenOrder }) {
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('created_desc');
  const [broadcastId, setBroadcastId] = useState('');
  const [broadcasts, setBroadcasts] = useState([]);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [partial, setPartial] = useState(null);
  const [labelIds, setLabelIds] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [failureDetail, setFailureDetail] = useState(null);  // {success, skipped, action}
  const [savedViews, setSavedViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [activeViewId, setActiveViewId] = useState(null);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const defaultApplied = useRef(false);

  const loadSavedViews = useCallback(async () => {
    try {
      const { data } = await api.get('/seller/oms/saved-views');
      setSavedViews(data.items || []);
      // Auto-apply default on first mount
      if (!defaultApplied.current) {
        defaultApplied.current = true;
        const def = (data.items || []).find((v) => v.is_default);
        if (def) applyView(def, false);
      }
    } catch { setSavedViews([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyView = (v, showToast = true) => {
    const f = v.filters || {};
    if (f.tab !== undefined) setTab(f.tab);
    if (f.sort !== undefined) setSort(f.sort);
    if (f.broadcast_id !== undefined) setBroadcastId(f.broadcast_id);
    if (f.limit !== undefined) setLimit(f.limit);
    setActiveViewId(v.view_id);
    if (showToast) toast.success(`보기 적용: ${v.name}`);
  };

  const saveView = async () => {
    if (!saveName.trim()) { toast.error('보기 이름을 입력해주세요.'); return; }
    try {
      const { data } = await api.post('/seller/oms/saved-views', {
        name: saveName.trim(),
        filters: { tab, sort, broadcast_id: broadcastId, limit },
        is_default: false,
      });
      toast.success(`저장됨: ${data.name}`);
      setSaveName(''); setSaveOpen(false); setActiveViewId(data.view_id);
      loadSavedViews();
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
  };

  const renameView = async (v) => {
    const nm = window.prompt('새 보기 이름', v.name);
    if (!nm || !nm.trim() || nm.trim() === v.name) return;
    try {
      await api.patch(`/seller/oms/saved-views/${v.view_id}`, { name: nm.trim() });
      toast.success('이름이 변경되었습니다.'); loadSavedViews();
    } catch (e) { toast.error(e?.response?.data?.detail || '변경 실패'); }
  };
  const deleteView = async (v) => {
    if (!window.confirm(`"${v.name}" 보기를 삭제할까요?`)) return;
    try {
      await api.delete(`/seller/oms/saved-views/${v.view_id}`);
      if (activeViewId === v.view_id) setActiveViewId(null);
      toast.success('삭제되었습니다.'); loadSavedViews();
    } catch (e) { toast.error(e?.response?.data?.detail || '삭제 실패'); }
  };
  const setAsDefault = async (v) => {
    try {
      await api.patch(`/seller/oms/saved-views/${v.view_id}`, { is_default: true });
      toast.success('기본 보기로 지정되었습니다.'); loadSavedViews();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };

  useEffect(() => {
    api.get('/seller/broadcasts').then((r) => setBroadcasts(r.data || [])).catch(() => setBroadcasts([]));
    loadSavedViews();
  }, [loadSavedViews]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, page, limit };
      if (tab && tab !== 'all') params.work_status = tab;
      if (q) params.q = q;
      if (broadcastId) params.broadcast_id = broadcastId;
      const [rL, rS] = await Promise.all([
        api.get('/seller/oms/orders', { params }),
        api.get('/seller/oms/summary'),
      ]);
      setRows(rL.data?.items || []);
      setTotal(rL.data?.total || 0);
      setTotalPages(rL.data?.total_pages || 1);
      setCounts(rS.data?.counts || {});
    } catch (e) { toast.error(e?.response?.data?.detail || '불러오기 실패'); }
    finally { setLoading(false); }
  }, [tab, q, sort, page, limit, broadcastId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [tab, q, sort, broadcastId, limit]);

  const toggleSelected = (sid) => {
    const s = new Set(selected);
    if (s.has(sid)) s.delete(sid); else s.add(sid);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.seller_order_id)));
  };
  const clearSelection = () => { setSelected(new Set()); setSelectMode(false); };

  const bulkStatus = async (targetStatus, label) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/seller/orders/bulk-status', {
        seller_order_ids: Array.from(selected),
        target_status: targetStatus,
        reason: `OMS 일괄 ${label}`,
      });
      const ok = (data.success_ids || []).length;
      const skipped = data.skipped || [];
      const fail = skipped.length;
      // Attach order metadata for failure detail
      const rowMap = new Map(rows.map((r) => [r.seller_order_id, r]));
      const enrichedSkipped = skipped.map((s) => ({
        ...s, row: rowMap.get(s.id),
      }));
      if (ok > 0 && fail === 0) toast.success(`${label} ${ok}건 처리 완료`);
      else if (ok > 0) {
        toast.success(`${label} ${ok}건 처리 · 실패 ${fail}건`);
        setFailureDetail({ action: label, success: ok, fail, items: enrichedSkipped });
      } else {
        toast.error(`${label} 처리 실패 ${fail}건`);
        setFailureDetail({ action: label, success: ok, fail, items: enrichedSkipped });
      }
      clearSelection();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '일괄 처리 실패'); }
    finally { setBulkBusy(false); }
  };

  const doPick = async (sid) => {
    try {
      await api.post(`/seller/fulfillment/${sid}/pick`);
      toast.success('피킹이 시작되었습니다.'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };

  // STEP 2-P7-AC · 3PL 물류센터 배정 (REUSE existing POST /seller/seller-orders/{sid}/assign · no new backend)
  // STEP 3-OPS-A · Bulk assignment via sequential-batched reuse of the same endpoint.
  const [assignSid, setAssignSid] = useState(null);           // single-mode target
  const [assignSids, setAssignSids] = useState(null);         // bulk-mode targets (array)
  const [assignBusy, setAssignBusy] = useState(false);
  const doAssign = async (sidOrSids, warehouseId, operatorId) => {
    const sids = Array.isArray(sidOrSids) ? sidOrSids : [sidOrSids];
    setAssignBusy(true);
    const successIds = [];
    const failures = [];
    const rowById = Object.fromEntries(rows.map((r) => [r.seller_order_id, r]));
    try {
      // 3-way parallel batches to avoid stampede on shared operator/warehouse links.
      const BATCH = 3;
      for (let i = 0; i < sids.length; i += BATCH) {
        const chunk = sids.slice(i, i + BATCH);
        // eslint-disable-next-line no-await-in-loop
        const settled = await Promise.allSettled(chunk.map(async (sid) => {
          const payload = { warehouse_id: warehouseId };
          if (operatorId) payload.operator_id = operatorId;
          await api.post(`/seller/seller-orders/${sid}/assign`, payload);
          return sid;
        }));
        settled.forEach((res, idx) => {
          const sid = chunk[idx];
          if (res.status === 'fulfilled') {
            successIds.push(sid);
          } else {
            failures.push({
              id: sid,
              reason: res.reason?.response?.data?.detail || res.reason?.message || '3PL 배정 실패',
              row: rowById[sid] || {},
            });
          }
        });
      }
    } finally {
      setAssignBusy(false);
    }
    // Result surfacing (canonical bulk UX: honest partial-failure disclosure).
    if (failures.length === 0) {
      toast.success(`3PL 물류센터에 ${successIds.length}건 배정되었습니다.`);
    } else if (successIds.length === 0) {
      toast.error(`3PL 배정 실패 · ${failures.length}건`);
      setFailureDetail({
        action: '3PL 물류센터 배정',
        success: 0,
        fail: failures.length,
        items: failures,
      });
    } else {
      toast(`3PL 배정 완료: ${successIds.length}건 · 실패: ${failures.length}건`, { icon: '⚠️' });
      setFailureDetail({
        action: '3PL 물류센터 배정',
        success: successIds.length,
        fail: failures.length,
        items: failures,
      });
    }
    // Deselect only the ones that succeeded (users may retry the failures).
    if (successIds.length > 0) {
      const nextSel = new Set(selected);
      successIds.forEach((s) => nextSel.delete(s));
      setSelected(nextSel);
    }
    setAssignSid(null);
    setAssignSids(null);
    load();
  };

  const resetFilters = () => { setTab('all'); setQ(''); setSort('created_desc'); setBroadcastId(''); setPage(1); setActiveViewId(null); };

  // ── Keyboard shortcuts (PC) ────────────────────────────────────────────
  useEffect(() => {
    const isTypingInField = () => {
      const t = document.activeElement;
      if (!t) return false;
      const tag = t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (t.isContentEditable) return true;
      return false;
    };
    const onKey = (e) => {
      if (isTypingInField()) return;  // Never intercept while typing
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        setFocusedIdx((i) => Math.min(rows.length - 1, i < 0 ? 0 : i + 1));
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        setFocusedIdx((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter' && focusedIdx >= 0 && rows[focusedIdx]) {
        e.preventDefault();
        onOpenOrder?.(rows[focusedIdx].seller_order_id);
      } else if (e.key === ' ' && focusedIdx >= 0 && rows[focusedIdx]) {
        e.preventDefault();
        setSelectMode(true);
        toggleSelected(rows[focusedIdx].seller_order_id);
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelectMode(true);
        toggleAll();
      } else if (e.key === 'Escape') {
        if (failureDetail) { setFailureDetail(null); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, focusedIdx, selected, failureDetail]);
  useEffect(() => { if (focusedIdx >= rows.length) setFocusedIdx(-1); }, [rows, focusedIdx]);

  const rangeStart = (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);

  return (
    <div className="space-y-3" data-testid="seller-oms-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>주문 작업대</h2>
          <p className="text-xs text-gray-500 mt-0.5">{total > 0 ? `${rangeStart}-${rangeEnd} / 총 ${total.toLocaleString()}건` : '주문 없음'}<span className="hidden md:inline text-[10px] text-gray-400 ml-2">· ↑↓ 이동 · Enter 상세 · Space 선택 · Cmd+A 전체선택</span></p>
        </div>
        <div className="flex items-center gap-2">
          {!selectMode ? (
            <button onClick={() => setSelectMode(true)} className="px-3 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold" data-testid="oms-select-mode">선택 모드</button>
          ) : (
            <button onClick={clearSelection} className="px-3 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold" data-testid="oms-select-cancel">선택 해제</button>
          )}
          <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold" data-testid="oms-refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''}/> 새로고침
          </button>
        </div>
      </div>

      {/* Saved views bar */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="oms-saved-views-bar">
        <div className="flex items-center gap-1 text-xs">
          <Bookmark size={13} className="text-gray-400"/>
          <span className="font-bold text-gray-500">저장된 보기:</span>
        </div>
        {savedViews.length === 0 ? (
          <span className="text-[11px] text-gray-400">아직 저장된 보기가 없습니다.</span>
        ) : savedViews.map((v) => (
          <div key={v.view_id} className={`inline-flex items-center gap-0.5 rounded-full border ${activeViewId === v.view_id ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'bg-white dark:bg-[#141414] border-black/10 dark:border-white/10'}`} data-testid={`oms-view-chip-${v.view_id}`}>
            <button onClick={() => applyView(v)} className="px-2.5 py-1 text-[11px] font-bold" data-testid={`oms-view-apply-${v.view_id}`}>
              {v.is_default && '★ '}{v.name}
            </button>
            <div className="dropdown relative group">
              <button className="px-1 py-1 opacity-70 hover:opacity-100 text-[10px]" data-testid={`oms-view-menu-${v.view_id}`}>⋯</button>
              <div className="hidden group-hover:block absolute right-0 top-full mt-0.5 z-20 bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 rounded shadow-lg min-w-[120px]">
                <button onClick={() => setAsDefault(v)} className="block w-full text-left px-2 py-1 text-[10px] hover:bg-black/5 dark:hover:bg-white/10 text-gray-700 dark:text-gray-200" data-testid={`oms-view-default-${v.view_id}`}>기본 보기로 설정</button>
                <button onClick={() => renameView(v)} className="block w-full text-left px-2 py-1 text-[10px] hover:bg-black/5 dark:hover:bg-white/10 text-gray-700 dark:text-gray-200" data-testid={`oms-view-rename-${v.view_id}`}>이름 변경</button>
                <button onClick={() => deleteView(v)} className="block w-full text-left px-2 py-1 text-[10px] hover:bg-black/5 dark:hover:bg-white/10 text-[#FF3B30]" data-testid={`oms-view-delete-${v.view_id}`}>삭제</button>
              </div>
            </div>
          </div>
        ))}
        {!saveOpen ? (
          <button onClick={() => setSaveOpen(true)} disabled={savedViews.length >= 20} className="px-2.5 py-1 rounded-full border border-dashed border-[#FF3B30]/50 text-[11px] font-bold text-[#FF3B30] disabled:opacity-40" data-testid="oms-view-save-open">+ 현재 조건 저장</button>
        ) : (
          <div className="inline-flex items-center gap-1">
            <input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="보기 이름"
              onKeyDown={(e) => e.key === 'Enter' && saveView()}
              className="px-2 py-1 rounded border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] text-[11px] w-40" data-testid="oms-view-save-name" autoFocus/>
            <button onClick={saveView} className="px-2 py-1 rounded bg-[#FF3B30] text-white text-[11px] font-bold" data-testid="oms-view-save-submit">저장</button>
            <button onClick={() => { setSaveOpen(false); setSaveName(''); }} className="px-2 py-1 text-[11px] text-gray-500">취소</button>
          </div>
        )}
      </div>

      {/* Quick status filter chips */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="oms-quick-filters">
        <button onClick={() => setTab('all')} className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${tab === 'all' ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'bg-white dark:bg-[#141414] border-black/10 dark:border-white/10'}`} data-testid="oms-chip-all">전체 <span className="opacity-70">{Object.values(counts).reduce((a,b) => a + (b||0), 0)}</span></button>
        {WORK_STATUS_ORDER.map((k) => (
          <button key={k} onClick={() => setTab(k)} className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${tab === k ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'bg-white dark:bg-[#141414] border-black/10 dark:border-white/10'}`} data-testid={`oms-chip-${k}`}>
            {WS_LABEL[k]} <span className="opacity-70">{counts[k] || 0}</span>
          </button>
        ))}
      </div>

      {/* Search + sort + broadcast + page size */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="주문번호 · 고객 · 전화 · 상품 · SKU · 송장 검색"
            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414]"
            data-testid="oms-search"/>
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-2 py-1.5 border rounded bg-white dark:bg-[#141414] border-black/10 dark:border-white/10 text-xs" data-testid="oms-sort">
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={broadcastId} onChange={(e) => setBroadcastId(e.target.value)} className="px-2 py-1.5 border rounded bg-white dark:bg-[#141414] border-black/10 dark:border-white/10 text-xs" data-testid="oms-broadcast-filter">
          <option value="">전체 주문</option>
          <option value="today_live">오늘 라이브</option>
          {broadcasts.map((b) => (<option key={b.schedule_id} value={b.schedule_id}>{b.title || b.schedule_id.slice(0, 12)}</option>))}
        </select>
        <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="px-2 py-1.5 border rounded bg-white dark:bg-[#141414] border-black/10 dark:border-white/10 text-xs" data-testid="oms-page-size">
          <option value={20}>20개씩</option><option value={50}>50개씩</option><option value={100}>100개씩</option>
        </select>
      </div>

      {selectMode && selected.size > 0 && (() => {
        // STEP 3-SHIP-D-3R HOTFIX · 선택 주문의 실제 상태로 벌크 버튼 정합성 판단.
        // 기존 backend transitions (paid→preparing / paid|preparing→shipping+tracking)
        // 은 그대로 두고, 프론트에서만 노출 조건을 좁혀 오해를 줄인다.
        const selRows = rows.filter((r) => selected.has(r.seller_order_id));
        const anyPaid       = selRows.some((r) => r.status === 'paid');
        const anyShippable  = selRows.some((r) => ['paid', 'preparing'].includes(r.status) && !!r.has_tracking);
        return (
        <div className="rounded-2xl border-2 border-[#FF3B30] bg-[#FF3B30]/5 p-2.5 flex items-center gap-2 flex-wrap" data-testid="oms-bulk-bar">
          <span className="text-xs font-black">선택 {selected.size}건</span>
          <div className="flex gap-1.5 flex-wrap">
            {anyPaid && (
              <button onClick={() => bulkStatus('preparing', '상품준비 처리')} disabled={bulkBusy}
                className="px-2.5 py-1 rounded bg-[#FF3B30] text-white text-[11px] font-bold disabled:opacity-60"
                data-testid="oms-bulk-preparing"
                title="결제완료(paid) 주문만 상품준비로 이동합니다.">상품준비 처리</button>
            )}
            <button onClick={() => setAssignSids(Array.from(selected))} disabled={bulkBusy || assignBusy}
              className="px-2.5 py-1 rounded bg-emerald-500 text-white text-[11px] font-bold disabled:opacity-60 inline-flex items-center gap-1"
              data-testid="oms-bulk-assign-3pl">
              <Warehouse size={11}/>3PL 일괄 배정
            </button>
            {anyShippable && (
              <button onClick={() => bulkStatus('shipping', '배송중 처리')} disabled={bulkBusy}
                className="px-2.5 py-1 rounded bg-blue-600 text-white text-[11px] font-bold disabled:opacity-60"
                data-testid="oms-bulk-shipping"
                title="송장이 등록된 주문만 배송중으로 전환됩니다.">배송중 처리</button>
            )}
            <button onClick={() => setLabelIds(Array.from(selected))} disabled={bulkBusy} className="px-2.5 py-1 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold disabled:opacity-60" data-testid="oms-bulk-labels">🖨 송장 출력</button>
            <button onClick={clearSelection} className="px-2.5 py-1 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold" data-testid="oms-bulk-clear">선택 해제</button>
          </div>
        </div>
        );
      })()}

      {loading ? (
        <div className="py-16 text-center text-sm text-gray-400"><Loader2 className="animate-spin inline mr-1" size={14}/> 불러오는 중…</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="oms-empty">
          <ClipboardList className="mx-auto text-gray-300" size={28} />
          <p className="mt-3 text-sm font-bold">조건에 맞는 주문이 없습니다.</p>
          <button onClick={resetFilters} className="mt-2 text-xs font-bold text-[#FF3B30] hover:underline" data-testid="oms-reset-filters">필터 초기화</button>
        </div>
      ) : (
        <>
          {selectMode && (
            <div className="hidden md:flex items-center gap-2 text-xs px-3">
              <input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={toggleAll} data-testid="oms-select-all" className="cursor-pointer" />
              <span className="text-gray-500">전체 선택 ({selected.size}/{rows.length})</span>
            </div>
          )}
          <div className="hidden md:block space-y-2" data-testid="oms-list">
            {rows.map((r, idx) => (
              <OrderRow key={r.seller_order_id} row={r}
                focused={idx === focusedIdx}
                selectMode={selectMode}
                checked={selected.has(r.seller_order_id)}
                onCheck={() => toggleSelected(r.seller_order_id)}
                onOpen={() => onOpenOrder?.(r.seller_order_id)}
                onPartial={() => setPartial({ seller_order_id: r.seller_order_id, order_number: r.order_number })}
                onPick={() => doPick(r.seller_order_id)}
                onAssign={() => setAssignSid(r.seller_order_id)}
                onLabel={() => setLabelIds([r.seller_order_id])}
              />
            ))}
          </div>
          <div className="md:hidden space-y-2" data-testid="oms-cards">
            {rows.map((r) => (
              <MobileCard key={r.seller_order_id} row={r} selectMode={selectMode}
                checked={selected.has(r.seller_order_id)} onCheck={() => toggleSelected(r.seller_order_id)}
                onOpen={() => onOpenOrder?.(r.seller_order_id)} onPick={() => doPick(r.seller_order_id)}
                onAssign={() => setAssignSid(r.seller_order_id)} />
            ))}
          </div>
        </>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 flex-wrap pt-2" data-testid="oms-pagination">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-2.5 py-1 rounded border border-black/10 dark:border-white/10 text-xs font-bold disabled:opacity-40" data-testid="oms-page-prev"><ChevronLeft size={12} className="inline"/> 이전</button>
          <span className="text-xs font-bold px-2">{page} / {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="px-2.5 py-1 rounded border border-black/10 dark:border-white/10 text-xs font-bold disabled:opacity-40" data-testid="oms-page-next">다음 <ChevronRight size={12} className="inline"/></button>
        </div>
      )}

      {failureDetail && (
        <FailureDetailModal detail={failureDetail} onClose={() => setFailureDetail(null)} onOpenOrder={onOpenOrder} />
      )}
      {partial && <PartialShipmentModal open={!!partial} sellerOrderId={partial.seller_order_id} orderNumber={partial.order_number} onClose={() => setPartial(null)} onChanged={load}/>}
      {labelIds && <ShippingLabelModal open={!!labelIds} orderIds={labelIds} onClose={() => setLabelIds(null)}/>}
      {assignSid && (
        <Assign3PLModal
          sid={assignSid}
          orderNumber={(rows.find((r) => r.seller_order_id === assignSid) || {}).order_number}
          busy={assignBusy}
          onClose={() => setAssignSid(null)}
          onSubmit={doAssign}
        />
      )}
      {assignSids && assignSids.length > 0 && (
        <Assign3PLModal
          sids={assignSids}
          busy={assignBusy}
          onClose={() => setAssignSids(null)}
          onSubmit={doAssign}
        />
      )}
      {failureDetail && (
        <FailureDetailModal
          detail={failureDetail}
          onClose={() => setFailureDetail(null)}
          onOpenOrder={onOpenOrder}
        />
      )}
    </div>
  );
}

function OrderRow({ row, focused, selectMode, checked, onCheck, onOpen, onPartial, onPick, onAssign, onLabel }) {
  const ws = row.work_status;
  const bc = row.broadcast_link;
  const hasShortage = row.has_shortage;
  const s = row.summary || {};
  const shortageSku = firstShortageSku(row);
  // STEP 2-P7-AC · 3PL assignment gating (reuses row.status + row.fulfillment_assignment_id)
  const canAssign3PL = ['paid', 'preparing'].includes(row.status) && !row.fulfillment_assignment_id;
  const isAssigned3PL = !!row.fulfillment_assignment_id;
  return (
    <div className={`rounded-2xl border-2 ${focused ? 'border-[#FF3B30] ring-2 ring-[#FF3B30]/20' : hasShortage ? 'border-rose-300 dark:border-rose-500/40' : 'border-black/10 dark:border-white/10'} bg-white dark:bg-[#141414] p-3`} data-testid={`oms-row-${row.seller_order_id}`}>
      <div className="flex items-start gap-3">
        {selectMode && (<input type="checkbox" checked={checked} onChange={onCheck} className="mt-1 cursor-pointer" data-testid={`oms-check-${row.seller_order_id}`} />)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={onOpen} className="font-mono text-sm font-black hover:text-[#FF3B30]" data-testid={`oms-open-${row.seller_order_id}`}>{row.order_number}</button>
            {bc && (<span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white" data-testid={`oms-live-${row.seller_order_id}`}>{bc.is_live ? '🔴 LIVE' : '📺'} {bc.title || '방송'}</span>)}
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-black ${WS_CLS[ws]}`}>{WS_LABEL[ws] || ws}</span>
            {hasShortage && (<span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-rose-500 text-white" data-testid={`oms-shortage-${row.seller_order_id}`}>재고부족</span>)}
          </div>
          <p className="text-[11px] text-gray-500 mt-0.5">{row.buyer_snapshot?.name || '-'} · {_mask(row.buyer_snapshot?.phone)} · {fmt(row.created_at)}</p>
          {hasShortage && (
            <div className="mt-1.5 rounded bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 p-1.5 text-[10px] text-rose-700 dark:text-rose-300" data-testid={`oms-shortage-detail-${row.seller_order_id}`}>
              {(row.items || []).filter((it) => it.fulfillment_status === 'waiting_stock' || it.fulfillment_status === 'out_of_stock').slice(0, 3).map((it, i) => (
                <p key={i} className="truncate">· {it.name_snapshot} {it.sku ? `[${it.sku}]` : ''} · 주문 {it.quantity} / 가용 {Math.max(0, (it.available_stock ?? 0))} / 부족 {Math.max(0, it.quantity - (it.available_stock ?? 0))}</p>
              ))}
              <div className="mt-1 flex gap-2">
                <a href={`?section=inventory&sku=${encodeURIComponent(shortageSku)}`} className="text-[10px] font-bold underline" data-testid={`oms-goto-inv-${row.seller_order_id}`}>재고관리</a>
                <span>·</span>
                <a href={`?section=purchase&sku=${encodeURIComponent(shortageSku)}`} className="text-[10px] font-bold underline" data-testid={`oms-goto-po-${row.seller_order_id}`}>발주관리</a>
              </div>
            </div>
          )}
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="text-sm font-black">{won((row.totals || {}).total || 0)}</p>
          <p className="text-[10px] text-gray-400">{s.total_items || 0}종 · 준비 {s.ready_items || 0}</p>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1 justify-end items-center">
        {isAssigned3PL && (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[11px] font-black border border-emerald-500/30"
            data-testid={`oms-3pl-assigned-${row.seller_order_id}`}
            title={`3PL 배정됨 · ${row.fulfillment_assignment_id}`}>
            <Warehouse size={10} className="inline"/> 3PL 배정됨
          </span>
        )}
        {(ws === 'partial_shippable' || ws === 'shippable' || ws === 'new_paid' || ws === 'waiting_stock' || ws === 'has_out_of_stock') && (
          <button onClick={onPartial} className="px-2 py-1 rounded border border-indigo-300 dark:border-indigo-500/30 text-indigo-700 dark:text-indigo-300 text-[11px] font-black" data-testid={`oms-partial-${row.seller_order_id}`}><Split size={10} className="inline mr-0.5"/>합포·부분출고</button>
        )}
        {canAssign3PL && (
          <button onClick={onAssign} className="px-2 py-1 rounded border border-emerald-400 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[11px] font-black" data-testid={`oms-assign-3pl-${row.seller_order_id}`}>
            <Warehouse size={10} className="inline mr-0.5"/>3PL 물류센터 배정
          </button>
        )}
        {(ws === 'shippable' || ws === 'new_paid') && !isAssigned3PL && (<button onClick={onPick} className="px-2 py-1 rounded bg-[#FF3B30] text-white text-[11px] font-black" data-testid={`oms-pick-${row.seller_order_id}`}><Play size={10} className="inline mr-0.5"/>피킹 시작</button>)}
        {row.has_tracking && (<button onClick={onLabel} className="px-2 py-1 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold" data-testid={`oms-label-${row.seller_order_id}`}>🖨 송장</button>)}
        <button onClick={onOpen} className="px-2 py-1 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold" data-testid={`oms-detail-${row.seller_order_id}`}>통합 상세</button>
      </div>
    </div>
  );
}

function MobileCard({ row, selectMode, checked, onCheck, onOpen, onPick, onAssign }) {
  const ws = row.work_status; const bc = row.broadcast_link;
  const canAssign3PL = ['paid', 'preparing'].includes(row.status) && !row.fulfillment_assignment_id;
  const isAssigned3PL = !!row.fulfillment_assignment_id;
  return (
    <div className="bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 p-3" data-testid={`oms-mrow-${row.seller_order_id}`}>
      <div className="flex justify-between items-start mb-1 gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {selectMode && <input type="checkbox" checked={checked} onChange={onCheck} className="mt-0.5" data-testid={`oms-mcheck-${row.seller_order_id}`} />}
          <div className="min-w-0">
            <p className="font-mono text-sm font-black">{row.order_number}</p>
            <p className="text-[11px] text-gray-500 truncate">{row.buyer_snapshot?.name || '-'} · {_mask(row.buyer_snapshot?.phone)}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-black ${WS_CLS[ws]}`}>{WS_LABEL[ws]}</span>
          {row.has_shortage && <span className="text-[9px] font-black px-1 py-0.5 rounded bg-rose-500 text-white">재고부족</span>}
        </div>
      </div>
      {bc && <p className="text-[10px] font-bold text-[#FF3B30] mb-1">{bc.is_live ? '🔴 LIVE' : '📺'} {bc.title}</p>}
      <p className="text-sm font-black">{won((row.totals || {}).total || 0)}</p>
      {isAssigned3PL && (
        <p className="mt-1 text-[10px] font-black text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1" data-testid={`oms-m-3pl-assigned-${row.seller_order_id}`}>
          <Warehouse size={10}/> 3PL 배정됨
        </p>
      )}
      <div className="flex gap-1 mt-2">
        <button onClick={onOpen} className="flex-1 py-1.5 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold" data-testid={`oms-m-open-${row.seller_order_id}`}>통합 상세</button>
        {canAssign3PL && (<button onClick={onAssign} className="flex-1 py-1.5 rounded bg-emerald-500 text-white text-[11px] font-black" data-testid={`oms-m-assign-3pl-${row.seller_order_id}`}>3PL 배정</button>)}
        {(ws === 'shippable' || ws === 'new_paid') && !isAssigned3PL && (<button onClick={onPick} className="flex-1 py-1.5 rounded bg-[#FF3B30] text-white text-[11px] font-black">피킹</button>)}
      </div>
    </div>
  );
}

// STEP 2-P7-AC · 3PL Assignment Modal (REUSE POST /seller/seller-orders/{sid}/assign · no new backend)
// STEP 3-OPS-A · Bulk mode — accepts `sids` (array) alongside single `sid`.
function Assign3PLModal({ sid, sids, orderNumber, busy, onClose, onSubmit }) {
  const bulk = Array.isArray(sids) && sids.length > 0;
  const [warehouseId, setWarehouseId] = useState('wh_hotfix_demo');
  const [operatorId, setOperatorId] = useState('');
  const canSubmit = warehouseId.trim().length > 0 && !busy;
  const submit = (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    onSubmit(bulk ? sids : sid, warehouseId.trim(), operatorId.trim() || null);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-white dark:bg-[#141414] rounded-2xl w-full max-w-md p-5 border border-black/10 dark:border-white/10"
        data-testid={bulk ? 'oms-assign-3pl-bulk-modal' : 'oms-assign-3pl-modal'}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-black inline-flex items-center gap-1">
            <Warehouse size={16} className="text-emerald-600"/>
            {bulk ? `3PL 일괄 배정 · 선택 ${sids.length}건` : '3PL 물류센터 배정'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-[#FF3B30]"><X size={18}/></button>
        </div>
        <p className="text-[11px] text-gray-500 mb-3">
          {bulk
            ? <>선택된 <span className="font-black">{sids.length}</span>건을 동일한 3PL 물류센터에 위탁합니다. 이미 다른 3PL에 배정된 주문은 backend 정책에 따라 새 창고/작업자로 <span className="font-bold">덮어쓰기</span>됩니다. 배정 후 판매자 피킹은 자동 차단됩니다.</>
            : <>주문 <span className="font-mono font-bold">{orderNumber || sid}</span> 을 3PL 물류센터에 위탁합니다. 배정 후 판매자 피킹은 자동 차단되고 물류센터 작업자 대시보드에서 픽업이 진행됩니다.</>}
        </p>
        <label className="block text-[11px] font-black mb-1">창고 ID (필수)</label>
        <input value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} placeholder="wh_hotfix_demo"
          className="w-full px-3 py-2 mb-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#0f0f0f]"
          data-testid="oms-assign-3pl-warehouse"/>
        <label className="block text-[11px] font-black mb-1">Operator ID (선택 · 3PL 창고면 자동 결정)</label>
        <input value={operatorId} onChange={(e) => setOperatorId(e.target.value)} placeholder="비워두면 창고 소유주로 자동 지정"
          className="w-full px-3 py-2 mb-3 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#0f0f0f]"
          data-testid="oms-assign-3pl-operator"/>
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="px-3 py-1.5 rounded-full text-xs font-bold border border-black/10 dark:border-white/10"
            data-testid="oms-assign-3pl-cancel">취소</button>
          <button type="submit" disabled={!canSubmit}
            className="px-3 py-1.5 rounded-full text-xs font-black bg-emerald-500 text-white disabled:opacity-50"
            data-testid="oms-assign-3pl-submit">
            {busy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : null}
            {bulk ? `선택 ${sids.length}건 배정` : '배정하기'}
          </button>
        </div>
      </form>
    </div>
  );
}

function FailureDetailModal({ detail, onClose, onOpenOrder }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-[#141414] rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5" data-testid="oms-failure-detail-modal">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-black">일괄 처리 결과</h3>
            <p className="text-xs text-gray-500 mt-0.5">{detail.action} · 성공 <span className="text-emerald-600 font-bold">{detail.success}</span>건 · 실패 <span className="text-[#FF3B30] font-bold">{detail.fail}</span>건</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-[#FF3B30]"><X size={18}/></button>
        </div>
        <p className="text-[11px] text-gray-500 mb-2">※ 성공한 주문은 롤백되지 않습니다. 실패 원인 확인 후 개별 처리하거나 조건을 조정해 다시 시도해주세요.</p>
        <div className="space-y-2" data-testid="oms-failure-list">
          {(detail.items || []).map((s, i) => {
            const r = s.row || {};
            const firstItem = (r.items || [])[0] || {};
            return (
              <div key={s.id + i} className="rounded border border-rose-200 dark:border-rose-500/30 bg-rose-50/40 dark:bg-rose-500/10 p-2" data-testid={`oms-fail-row-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-sm font-black">{r.order_number || s.id}</p>
                    <p className="text-[11px] text-gray-500 truncate">{r.buyer_snapshot?.name || '-'} · {firstItem.name_snapshot || '-'}{firstItem.sku ? ` [${firstItem.sku}]` : ''}</p>
                    <p className="text-[10px] mt-1"><span className="font-bold text-gray-500">시도한 처리:</span> {detail.action}</p>
                    <p className="text-[11px] font-bold text-[#FF3B30] mt-0.5">실패: {s.reason || '알 수 없는 사유'}</p>
                  </div>
                  <button onClick={() => { onClose(); onOpenOrder?.(s.id); }} className="flex-shrink-0 px-2 py-1 rounded bg-[#FF3B30] text-white text-[11px] font-bold" data-testid={`oms-fail-open-${s.id}`}>주문 상세</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
